import { UserRef } from './bewerber';

// Kennenlernen Status enum matching backend
export enum KennenlernenStatus {
  GEPLANT = 'GEPLANT',
  ERLEDIGT = 'ERLEDIGT',
  ABGESAGT = 'ABGESAGT',
}

// Kennenlernen Ergebnis enum matching backend
export enum KennenlernenErgebnis {
  POSITIV = 'POSITIV',
  NEGATIV = 'NEGATIV',
  NEUTRAL = 'NEUTRAL',
  OFFEN = 'OFFEN',
}

// Stadt reference for Kennenlernen
export interface StadtRef {
  id: string;
  name: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Bedarf reference for Kennenlernen
export interface BedarfRefKennenlernen {
  id: string;
  childFirstName: string;
  childLastName: string;
  schoolName: string | null;
  schoolCity: StadtRef | string | null;
  childCity?: string | null;
}

// Bewerber reference for Kennenlernen
export interface BewerberRefKennenlernen {
  id: string;
  firstName: string;
  lastName: string;
  city: { name: string } | string | null;
  ortStelleIds: string[];
  email?: string | null;
  phone?: string | null;
  status?: string;
}

// Kennenlernen interface (full)
export interface Kennenlernen {
  id: string;
  bedarfId: string;
  bewerberId: string;
  scheduledAt: string;
  location: string | null;
  status: KennenlernenStatus;
  outcome: KennenlernenErgebnis;
  feedbackParents: string | null;
  feedbackCandidate: string | null;
  notes: string | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  bedarf?: BedarfRefKennenlernen;
  bewerber?: BewerberRefKennenlernen;
  createdBy?: UserRef;
  zuordnungen?: unknown[];
}

// Create Kennenlernen DTO
export interface CreateKennenlernenDto {
  bedarfId: string;
  bewerberId: string;
  scheduledAt: string;
  location?: string;
  notes?: string;
  sendEmails?: boolean;
}

// Update Kennenlernen DTO
export interface UpdateKennenlernenDto {
  scheduledAt?: string;
  location?: string;
  status?: KennenlernenStatus;
  outcome?: KennenlernenErgebnis;
  feedbackParents?: string;
  feedbackCandidate?: string;
  notes?: string;
}

// Query parameters for listing
export interface QueryKennenlernenDto {
  bedarfId?: string;
  bewerberId?: string;
  status?: KennenlernenStatus;
  location?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
  sortBy?: 'scheduledAt' | 'createdAt' | 'status';
  sortOrder?: 'asc' | 'desc';
}

// Kennenlernen statistics
export interface KennenlernenStatistics {
  total: number;
  byStatus: Record<string, number>;
  upcoming: number;
}

// Status display configuration
export const KennenlernenStatusConfig: Record<KennenlernenStatus, { label: string; color: string; bgColor: string }> = {
  [KennenlernenStatus.GEPLANT]: { label: 'Geplant', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  [KennenlernenStatus.ERLEDIGT]: { label: 'Erledigt', color: 'text-green-700', bgColor: 'bg-green-100' },
  [KennenlernenStatus.ABGESAGT]: { label: 'Abgesagt', color: 'text-red-700', bgColor: 'bg-red-100' },
};

// Ergebnis display configuration
export const KennenlernenErgebnisConfig: Record<KennenlernenErgebnis, { label: string; color: string; bgColor: string }> = {
  [KennenlernenErgebnis.POSITIV]: { label: 'Positiv', color: 'text-green-700', bgColor: 'bg-green-100' },
  [KennenlernenErgebnis.NEGATIV]: { label: 'Negativ', color: 'text-red-700', bgColor: 'bg-red-100' },
  [KennenlernenErgebnis.NEUTRAL]: { label: 'Neutral', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  [KennenlernenErgebnis.OFFEN]: { label: 'Offen', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
};

// Status order for Kanban
export const KENNENLERNEN_STATUS_ORDER: KennenlernenStatus[] = [
  KennenlernenStatus.GEPLANT,
  KennenlernenStatus.ERLEDIGT,
  KennenlernenStatus.ABGESAGT,
];

// Helper to format Kennenlernen date/time
export const formatKennenlernenDateTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Helper to format Kennenlernen date only
export const formatKennenlernenDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Helper to get child full name
export const getChildName = (kennenlernen: Kennenlernen): string => {
  if (!kennenlernen.bedarf) return 'Unbekannt';
  return `${kennenlernen.bedarf.childFirstName} ${kennenlernen.bedarf.childLastName}`;
};

// Helper to get bewerber full name
export const getBewerberName = (kennenlernen: Kennenlernen): string => {
  if (!kennenlernen.bewerber) return 'Unbekannt';
  return `${kennenlernen.bewerber.firstName} ${kennenlernen.bewerber.lastName}`;
};

// Matching Bewerber type (for Bedarf matching endpoint)
export type MatchType =
  | 'WOHNORT'
  | 'ORTSTELLE'
  | 'BOTH'
  | 'BEWERBUNG'
  | 'UMKREIS';

/** How the matching list picks its candidates. */
export type MatchingMode = 'CITY' | 'RADIUS';

/** How precisely an address could be located. */
export type GeoPrecision = 'EXACT' | 'POSTAL_CODE' | 'CITY' | 'NONE';

export const RADIUS_OPTIONS = [10, 25, 50, 75] as const;
export const DEFAULT_RADIUS_KM = 25;

/** Page size of the Umkreis list; the Stadt list stays unpaged. */
export const MATCHING_PAGE_SIZE = 50;

export interface MatchingBewerber {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  city: string | null;
  ortStellen: string[];
  status: string;
  hasInvitationEmails: boolean;
  matchType: MatchType;
  /** Applied to exactly this Bedarf. */
  beworbenAufDiesenBedarf: boolean;
  /** Applied to a different Bedarf of the same school. */
  beworbenAufGleicheSchule: boolean;
  /** The Bedarf applied to — for the badge's tooltip and link. */
  beworbenAufBedarfId: string | null;
  beworbenAufBedarfName: string | null;
  /** Flagged as not suitable for this specific Bedarf (per Bedarf-Bewerber pair). */
  nichtGeeignet: boolean;
  /** Assigned to exactly this Bedarf — the Schulbegleiter on this position. */
  zugeordnet: boolean;
  /** Assigned to a different Bedarf; the row says where instead of offering a button. */
  zugeordnetAufBedarfId: string | null;
  zugeordnetAufBedarfName: string | null;
  /** This Bedarf was already sent to the Bewerber via email (derived from EmailLog). */
  bedarfGesendet: boolean;
  bedarfGesendetAt: string | null;
  /** Straight-line distance to the school. Null outside Umkreis mode. */
  distanceKm: number | null;
  /** How precisely this Bewerber's own address could be located. */
  geoPrecision: GeoPrecision;
  maxPendelKm: number | null;
  /** Informational only — a Bewerber is never hidden by their own commute limit. */
  exceedsMaxPendelKm: boolean;
  /** How many hours a week the Bewerber wants to work, from the interview. */
  minWochenstunden: number | null;
  maxWochenstunden: number | null;
  /** Set once someone requested the travel time for this pair. */
  routeDistanceKm: number | null;
  routeDurationMin: number | null;
}

export interface MatchingBewerberMeta {
  mode: MatchingMode;
  radiusKm: number | null;
  /** Total matches found, which may exceed the returned page. */
  total: number;
  schoolGeocoded: boolean;
  schoolPrecision: GeoPrecision;
}

/**
 * The matching endpoint carries a `meta` of its own shape, so it cannot reuse
 * the shared `ApiResponse<T>` whose meta is typed for pagination.
 */
export interface MatchingBewerberResponse {
  success: boolean;
  data: MatchingBewerber[];
  meta: MatchingBewerberMeta;
}

// Match type display configuration
export const MatchTypeConfig: Record<MatchType, { label: string; description: string }> = {
  WOHNORT: { label: 'Wohnort', description: 'Wohnt in diesem Ort' },
  ORTSTELLE: { label: 'Ort Stelle', description: 'Hat sich für diesen Ort beworben' },
  BOTH: { label: 'Beides', description: 'Wohnt hier und hat sich beworben' },
  BEWERBUNG: { label: 'Bewerbung', description: 'Hat sich auf diese Stelle beworben' },
  UMKREIS: { label: 'Umkreis', description: 'Liegt im gewählten Umkreis der Schule' },
};

/** Display configuration for the geocoding precision of a distance. */
export const GeoPrecisionConfig: Record<
  GeoPrecision,
  { label: string; className: string; description: string }
> = {
  EXACT: {
    label: 'Adresse genau',
    className: 'bg-green-100 text-green-800',
    description: 'Straße, PLZ und Ort konnten exakt verortet werden',
  },
  POSTAL_CODE: {
    label: 'PLZ-Ebene',
    className: 'bg-amber-100 text-amber-800',
    description: 'Nur die Postleitzahl konnte verortet werden — Entfernung ist ungefähr',
  },
  CITY: {
    label: 'nur Stadt-Ebene',
    className: 'bg-orange-100 text-orange-800',
    description: 'Nur der Ort konnte verortet werden — Entfernung ist grob',
  },
  NONE: {
    label: 'Adresse fehlt',
    className: 'bg-gray-100 text-gray-600',
    description: 'Keine verwertbare Adresse hinterlegt',
  },
};
